import React, { useState } from 'react';
import { Box, Button, IconButton, InputBase, Typography, Chip, useTheme } from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import { makeStyles } from '@mui/styles';
import MicRecorderButton from '../../../../components/AudioRecorder/MicRecorderButton';
import { translate } from '../../../../translations/translator';
import FileUploadButton from 'components/FileUploadButton';
import { faFileAudio } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
const t = key => translate('assignmentTracker', key);

type Props = {
  onSend: (text: string) => void;
  onSendAudio: (audio: File) => void;
};

export const MessageInput: React.FC<Props> = ({ onSend, onSendAudio }) => {
  const [text, setText] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const theme = useTheme();
  const classes = useStyles(theme)();

  const onClickSend = () => {
    if (file) {
      onSendAudio(file);
      setFile(null);
    }

    if (text) {
      onSend(text);
      setText('');
    }
  };

  const onSubmit = event => {
    event.preventDefault();
    if (text) {
      onSend(text);
      setText('');
    }
  };

  const onFileUpload = ({ target }) => {
    setFile(target.files[0]);
  };

  const maybeShowFileIcon = file && {
    inputComponent: () => (
      <Chip
        onDelete={() => setFile(null)}
        color="primary"
        label={
          <>
            {file.name}
            <FontAwesomeIcon icon={faFileAudio} size="sm" style={{ margin: '0 5px' }} />
          </>
        }
      />
    )
  };

  return (
    <>
      <Typography variant="body1">{t('yourMessage')}</Typography>
      <form autoComplete="off" noValidate onSubmit={onSubmit}>
        <InputBase
          value={text}
          onChange={event => setText(event.target.value)}
          fullWidth
          classes={{ root: classes.inputRoot }}
          inputProps={{
            maxLength: '1024px'
          }}
          {...maybeShowFileIcon}
        />
      </form>
      <Box py={3} display="flex" alignItems="center" justifyContent="flex-end">
        <Box mr={2} border={1} borderRadius={8}>
          <FileUploadButton
            btnClass={classes.fileUpload}
            color="#2878C1"
            variant="text"
            name="audioFiles"
            ButtonComponent={props => (
              <IconButton
                {...props}
                size="small"
                color="inherit"
                sx={{
                  borderRadius: 8
                }}
              />
            )}
            onChange={onFileUpload}
            labelProps={{ style: { margin: 0 } }}
            value={''}
          >
            <PublishIcon fontSize="small" />
          </FileUploadButton>
        </Box>

        <MicRecorderButton
          maxRecordTime={120}
          onSendMessage={onSendAudio}
          teacherInstructions={true}
        />
        <Button
          onClick={onClickSend}
          className={classes.sendButton}
          size="small"
          variant="contained"
          color="primary"
        >
          {t('send')}
        </Button>
      </Box>
    </>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    inputRoot: {
      boxSizing: 'border-box',
      height: '48px',
      border: '2px solid',
      borderRadius: '4px'
    },
    sendButton: {
      textTransform: 'none',
      backgroundColor: theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: theme.palette.secondary.light
      }
    },
    fileUpload: {
      margin: '0 !important',
      padding: '5px !important',
      textTransform: 'none',
      fontSize: '14px',
      backgroundColor: '#fff',
      whiteSpace: 'nowrap'
    }
  }));
